// Procurement view of the SHEET 27 order data — the cumulative figures the yard's Excel totals row and
// "ADDITIONAL REQUESTS" tab carry: how many boats need each option / engine / colour, and every
// vessel's additional requests in one list, plus the Seakeepers written in anywhere on the sheet.
// The Warehouse tab is the same view with a Watch-it anti-collision box beside the Seakeepers (both have
// no column on the sheet, so they're found in whatever cell they were written, however spelled).
// Read-only; the numbers come straight from the order sheet.

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useOrderSheet, useOverview } from '../features/hooks.js';
import { orderSheetRoster } from '../features/orderSheetImport.js';
import { buildAdditionalRequests, buildTally, countSeakeepers, countWatchIts, type OptionTally, type ProcVessel, type WrittenCount, type WrittenVessel } from '../features/procurement.js';
import { exportProcurementXlsx } from '../features/exportProcurement.js';
import { ErrorState, InlineError, LoadingState } from './ui.js';
import { IconDownload } from './icons.js';

/** something written in by hand that the view counts (a Seakeeper, a Watch-it), and how it reads. */
interface WrittenItem {
  id: string;
  count: WrittenCount;
  /** its name in the box and the list's heading; `one` for a single unit ("Seakeeper") */
  name: string;
  one?: string;
  /** a boat's line in the list's own column (the Seakeeper model), and its heading; none for an item a
   *  boat takes one of (the Watch-it) */
  unit?: (v: WrittenVessel) => ReactNode;
  unitHead?: string;
  /** chips of the units by model above the list (Seakeepers come in models) */
  byModel: boolean;
}

/** the KPI box for a written-in item: how many, and it opens or closes its list. */
function WrittenKpi({ item, open, onToggle }: { item: WrittenItem; open: boolean; onToggle: () => void }) {
  const { count } = item;
  const name = count.units === 1 && item.one ? item.one : item.name;
  if (!count.vessels.length) {
    return (
      <div className="proc-kpi" title={`No ${item.name} written anywhere on these vessels' order sheets`}>
        <div className="n">0</div><div className="l">{name}</div>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`proc-kpi proc-kpi-btn${open ? ' on' : ''}`}
      aria-expanded={open}
      aria-controls={`proc-${item.id}`}
      title={open ? 'Hide the list of vessels' : 'Show which vessels: SAP, name, model and what was written'}
      onClick={onToggle}
    >
      <span className="n">{count.units}</span>
      <span className="l">
        {name}
        {count.units !== count.vessels.length && ` · ${count.vessels.length} vessel${count.vessels.length === 1 ? '' : 's'}`}
      </span>
    </button>
  );
}

/** a written-in item's list: each boat with its SAP, name and model, and what was written where. */
function WrittenCard({ item }: { item: WrittenItem }) {
  const { count, name } = item;
  return (
    <div className="proc-card proc-sk" id={`proc-${item.id}`}>
      <div className="proc-card-hd">
        {name}
        <span className="c">{item.unit ? `${count.units} on ` : ''}{count.vessels.length} vessel{count.vessels.length === 1 ? '' : 's'}</span>
      </div>
      {item.byModel && (
        <div className="proc-sk-models">
          {count.byModel.map((m) => (
            <span key={m.model} className={`proc-chip${m.model ? '' : ' mark'}`}>
              {m.model ? `${item.one ?? name} ${m.model}` : 'model not given'} <b>{m.units}</b>
            </span>
          ))}
        </div>
      )}
      <div className="tbl-wrap proc-sk-tbl">
        <table className="tbl tbl-cards">
          <thead>
            <tr><th>SAP</th><th>Name</th><th>Model</th>{item.unit && <th>{item.unitHead}</th>}<th>Written on the sheet</th></tr>
          </thead>
          <tbody>
            {count.vessels.map((v) => (
              <tr key={v.key ?? v.sap}>
                <td className="tc-lbl proc-sk-sap" data-label="SAP">{v.sap || <span className="muted">no SAP</span>}</td>
                <td className="tc-title">{v.name}</td>
                <td className="tc-lbl" data-label="Model">{v.model || <span className="muted">-</span>}</td>
                {item.unit && <td className="tc-end proc-sk-unit">{item.unit(v)}</td>}
                <td className="tc-meta">
                  <ul className="proc-sk-said">
                    {v.mentions.map((m) => <li key={m.key}><span className="k">{m.label}</span>{m.text}</li>)}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function OrderSheetProcurement({ season, view = 'procurement' }: { season: string; view?: 'procurement' | 'warehouse' }) {
  const overview = useOverview(season || undefined);
  const sheetQ = useOrderSheet();
  const [model, setModel] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<unknown>(null);
  // each written-in list (SAP, name, model) shows from the start; its box hides and shows it
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set());
  const toggleList = (id: string) => setClosed((c) => { const n = new Set(c); if (!n.delete(id)) n.add(id); return n; });
  const warehouse = view === 'warehouse';

  // drill-down: hovering a value chip previews its vessels; clicking a chip pins that list open so you
  // can read/scroll it. The visible list is the hovered chip if any, otherwise the pinned one. Chip ids
  // are `${column}${value}` so each value across the whole rollup is addressable.
  // The preview is for a mouse (and keyboard focus) only: a tap just pins, so a second tap closes it.
  const SEP = '';
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const leaveTimer = useRef<number | null>(null);
  const openId = hovered ?? pinned;
  const enter = (id: string) => {
    if (leaveTimer.current) { window.clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setHovered(id);
  };
  const leave = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => setHovered(null), 160); // small grace so moving to the panel doesn't flicker
  };
  const togglePin = (id: string) => {
    setHovered(null);
    setPinned((p) => (p === id ? null : id));
  };
  const mouse = (e: { pointerType: string }) => e.pointerType === 'mouse';

  const sheet = sheetQ.data?.sheet ?? {};
  const hidden = useMemo(() => new Set(sheetQ.data?.hidden ?? []), [sheetQ.data]);
  // same roster as the grid: only the vessels actually on the order sheet (uploaded or hand-entered),
  // not the whole overview, minus departed ("Left") boats and anything removed by hand. Keyed by key.
  const allVessels: ProcVessel[] = useMemo(
    () => orderSheetRoster(overview.data?.vessels ?? [], sheet, hidden)
      .map((v) => ({ key: v.key, sap: v.sap, model: v.model, name: v.name })),
    [overview.data, sheet, hidden],
  );
  const models = useMemo(() => Array.from(new Set(allVessels.map((v) => v.model).filter(Boolean))).sort(), [allVessels]);
  const vessels = useMemo(() => (model ? allVessels.filter((v) => v.model === model) : allVessels), [allVessels, model]);
  const tally = useMemo(() => buildTally(vessels, sheet), [vessels, sheet]);
  const requests = useMemo(() => buildAdditionalRequests(vessels, sheet), [vessels, sheet]);
  const seakeepers = useMemo(() => countSeakeepers(vessels, sheet), [vessels, sheet]);
  const watchIts = useMemo(() => (warehouse ? countWatchIts(vessels, sheet) : null), [warehouse, vessels, sheet]);
  const written: WrittenItem[] = [
    {
      id: 'seakeepers',
      count: seakeepers,
      name: 'Seakeepers',
      one: 'Seakeeper',
      unitHead: 'Seakeeper',
      byModel: true,
      unit: (v) => (
        <>
          {v.qty > 1 && <b className="proc-sk-qty">{v.qty} ×</b>}
          {v.models.length ? `Seakeeper ${v.models.join(' / ')}` : <span className="muted">model not given</span>}
        </>
      ),
    },
    ...(watchIts ? [{
      id: 'watchit',
      count: watchIts,
      name: 'Watch-it anti-collision',
      byModel: false,
    }] : []),
  ];

  // group the option tally into its layout sections, preserving order.
  const sections = useMemo(() => {
    const out: Array<{ name: string; rows: OptionTally[] }> = [];
    for (const t of tally) {
      const last = out[out.length - 1];
      if (last && last.name === t.section) last.rows.push(t);
      else out.push({ name: t.section, rows: [t] });
    }
    return out;
  }, [tally]);

  if (overview.isLoading || sheetQ.isLoading) return <LoadingState label="Building procurement list…" />;
  // only a failed first load replaces the rollup; a failed background refetch keeps the cached figures
  const loadErr = (!overview.data && overview.error) || (!sheetQ.data && sheetQ.error);
  if (loadErr) return <ErrorState error={loadErr} onRetry={() => { void overview.refetch(); void sheetQ.refetch(); }} />;

  const denom = Math.max(1, vessels.length);
  const doExport = async () => {
    setExporting(true);
    try {
      setExportErr(null);
      await exportProcurementXlsx(vessels, sheet, `${warehouse ? 'Warehouse' : 'Procurement'} - ${model || 'all models'}`, { watchIt: warehouse });
    } catch (e) {
      setExportErr(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <span className="field-label" style={{ margin: 0, fontSize: 13 }}>{warehouse ? 'Warehouse rollup' : 'Procurement rollup'}</span>
        <span style={{ flex: 1 }} />
        <select className="field" style={{ fontSize: 12 }} aria-label="Model" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <button className="btn btn-sm btn-primary" disabled={exporting || !vessels.length} onClick={() => void doExport()}>
          {exporting ? 'Exporting…' : <><IconDownload size={14} /> Export to Excel</>}
        </button>
      </div>
      {exportErr != null && <InlineError error={exportErr} onDismiss={() => setExportErr(null)} />}

      <div className="proc-kpis">
        <div className="proc-kpi accent"><div className="n">{vessels.length}</div><div className="l">Vessels{model ? ` · ${model}` : ''}</div></div>
        <div className="proc-kpi"><div className="n">{tally.length}</div><div className="l">Options in use</div></div>
        <div className="proc-kpi"><div className="n">{requests.length}</div><div className="l">With extra requests</div></div>
        {/* no column for these on the sheet: found in whatever cell they were written, however spelled */}
        {written.map((w) => <WrittenKpi key={w.id} item={w} open={!closed.has(w.id)} onToggle={() => toggleList(w.id)} />)}
      </div>
      {written.filter((w) => !closed.has(w.id) && w.count.vessels.length > 0).map((w) => <WrittenCard key={w.id} item={w} />)}

      <div className="proc-grid">
        {/* ── option tally ─────────────────────────────────────────────── */}
        <div className="proc-card">
          <div className="proc-card-hd">Options <span className="c">boats needing each</span></div>
          <div className="proc-card-bd">
            {sections.length === 0 && (
              <div className="proc-empty">No order data yet - open the “Order sheet” tab and use “Import from Excel” to load SHEET 27.</div>
            )}
            {sections.map((sec) => (
              <div key={sec.name} className="proc-sec-group">
                <div className="proc-sec">{sec.name}</div>
                {sec.rows.map((t) => {
                  const openVal = openId && openId.startsWith(t.key + SEP) ? openId.slice((t.key + SEP).length) : null;
                  const openEntry = openVal != null ? t.values.find((x) => x.value === openVal) ?? null : null;
                  const chip = (x: (typeof t.values)[number], label: string) => {
                    const id = t.key + SEP + x.value;
                    return (
                      <button
                        key={x.value}
                        type="button"
                        className={`proc-chip${t.plainMark ? ' mark' : ''}${openId === id ? ' on' : ''}`}
                        title="Click to see which vessels"
                        aria-pressed={pinned === id}
                        onPointerEnter={(e) => { if (mouse(e)) enter(id); }}
                        onPointerLeave={(e) => { if (mouse(e)) leave(); }}
                        onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) enter(id); }}
                        onBlur={leave}
                        onClick={() => togglePin(id)}
                      >
                        {label} <b>{x.count}</b>
                      </button>
                    );
                  };
                  return (
                    <div key={t.key} className="proc-opt-wrap">
                      <div className="proc-opt">
                        <div className="lbl">{t.label.replace(/:$/, '')}</div>
                        <div className="proc-meter" title={`${t.total} of ${vessels.length} vessels`}>
                          <span className="proc-bar"><i style={{ width: `${Math.round((t.total / denom) * 100)}%` }} /></span>
                          <span className="proc-n">{t.total}</span>
                        </div>
                        <div className="proc-vals">
                          {t.plainMark
                            ? t.values.map((x) => chip(x, 'selected'))
                            : t.values.map((x) => chip(x, x.value === '+' ? 'yes' : x.value))}
                        </div>
                      </div>
                      {openEntry && (
                        <div
                          className="proc-drill"
                          onPointerEnter={(e) => { if (mouse(e)) enter(t.key + SEP + openEntry.value); }}
                          onPointerLeave={(e) => { if (mouse(e)) leave(); }}
                        >
                          <div className="proc-drill-hd">
                            {t.label.replace(/:$/, '')} · {openEntry.value === '+' ? (t.plainMark ? 'selected' : 'yes') : openEntry.value}
                            <span className="c">{openEntry.count} vessel{openEntry.count === 1 ? '' : 's'}</span>
                          </div>
                          <div className="proc-drill-list">
                            {openEntry.vessels.map((v) => (
                              <span key={v.key ?? v.sap} className="proc-drill-v">{v.name}<i>{[v.sap || 'no SAP', v.model].filter(Boolean).join(' · ')}</i></span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* ── additional requests ──────────────────────────────────────── */}
        <div className="proc-card">
          <div className="proc-card-hd">Additional requests <span className="c">{requests.length} vessel{requests.length === 1 ? '' : 's'}</span></div>
          <div className="proc-card-bd">
            {requests.length === 0 && <div className="proc-empty">No additional requests.</div>}
            {requests.map((r) => (
              <div key={r.sap || r.name} className="proc-req">
                <div className="v">{r.name}</div>
                <div className="sub">{[r.sap || 'no SAP', r.model].filter(Boolean).join(' · ')}</div>
                <ol>{r.requests.map((req, i) => <li key={i}>{req}</li>)}</ol>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
